import { useMemo } from "react";
import { SingleDesignation } from "react-migration/domains/constraints/typings/applicationTypes/SingleDesignation";
import { VEHICLE_TYPES, VehicleType } from "./constants";

export const useTrafficCounts = (designationData: SingleDesignation) => {
  const yearlyTrafficData = useMemo(() => {
    const typeToYearMap: Record<number, Record<string, number>> = {};

    designationData.designation_attributes.forEach((attr) => {
      if (!attr.value) return;

      const parts = attr.key.split("_");
      if (parts.length > 1) {
        const yearStr = parts.pop();
        const vehicleType = parts.join("_") as VehicleType;
        if (VEHICLE_TYPES.includes(vehicleType) && yearStr && /^\d{4}$/.test(yearStr)) {
          const year = parseInt(yearStr, 10);
          if (!typeToYearMap[year]) typeToYearMap[year] = {};
          typeToYearMap[year][vehicleType] = parseInt(attr.value, 10);
        }
      }
    });

    const years = Object.keys(typeToYearMap)
      .map((y) => parseInt(y, 10))
      .sort((a, b) => a - b);

    const data = years.map((year, idx) => {
      const prevYear = years[idx - 1];
      const total = VEHICLE_TYPES.filter((v) => v !== "all_motor_vehicles").reduce(
        (sum, v) => sum + (typeToYearMap[year][v] || 0),
        0
      );
      const prevTotal = prevYear
        ? VEHICLE_TYPES.filter((v) => v !== "all_motor_vehicles").reduce(
            (sum, v) => sum + (typeToYearMap[prevYear][v] || 0),
            0
          )
        : null;
      const pctChange = prevTotal && prevTotal > 0 ? ((total - prevTotal) / prevTotal) * 100 : null;

      return {
        year,
        vehicles: VEHICLE_TYPES.reduce(
          (acc, v) => ({ ...acc, [v]: typeToYearMap[year][v] || 0 }),
          {} as Record<VehicleType, number>
        ),
        total,
        percentageChange: pctChange !== null ? Number(pctChange.toFixed(2)) : null,
      };
    });

    return data.sort((a, b) => b.year - a.year);
  }, [designationData]);

  const latestTrafficData = useMemo(() => {
    return yearlyTrafficData.length > 0 ? yearlyTrafficData[0] : null;
  }, [yearlyTrafficData]);

  return {
    yearlyTrafficData,
    latestTrafficData,
  };
};
